import { AI_SCREEN_CONTENTS } from "./screen-content.js";
import { z } from "zod";
import { GROUP_ROLE_KEYS } from "../group-role-labels.js";

/**
 * 🔴 US-AIV2-0011 (@UC0150 @UC0158 @UC0159) — SỔ NĂNG LỰC: hình dạng của MỘT TRANG.
 *
 * ADR-0009: trợ lý v2 = một Sổ + ba vai. *Một trang = một việc*; mọi thứ khác (tool cho LLM, câu hỏi
 * ô, phiếu xác nhận, thẻ dẫn đường, câu "cách dùng", bộ eval) **suy ra** từ trang — không file nào
 * ngoài Sổ được khai tool/usecase/thẻ/câu ví dụ (`@UC0150`, gate `scripts/gen-ai-registry.mjs`).
 *
 * Vì sao `.strict()` ở MỌI object: bất biến của v2 được **đúc vào cấu trúc**, không vào prompt (ADR
 * §"Bất biến đúc vào CẤU TRÚC"). Một trang lỡ khai field `amount`/`memberId` — tức LLM có thể trả
 * giá trị thay vì span — phải **không biểu diễn được** ngay ở biên, không phải bị review bắt.
 *
 * Vì sao Sổ nằm ở `packages/domain` (thuần) chứ không ở `apps/api`: Sổ là DỮ LIỆU đọc bởi ba nơi —
 * engine (api), bộ sinh bảng `ai-registry.md` (scripts), và bộ eval (0053). Đặt ở api thì hai nơi
 * kia phải import Nest. `zod` được thêm vào domain đúng cho việc này (16/09/2026; contracts đã dùng
 * cùng phiên bản) — domain vẫn không framework, không DB.
 */

/** Bốn tier (ADR-0009 bất biến 5): `read` không phiếu · `T1` một chạm · `T2` phiếu đầy đủ · `redirect` chỉ nút mở màn hình. */
export const AI_PAGE_TIERS = ["read", "T1", "T2", "redirect"] as const;
export type AiPageTier = (typeof AI_PAGE_TIERS)[number];

/**
 * Trạng thái trang — THỨ TỰ LÀ RATCHET (`@UC0159`): chỉ đi lên `draft → bound → evaluated → live`.
 * `live` đòi eval xanh; gate so với bảng đã commit nên hạ một bậc là đỏ, kể cả hạ "tạm".
 */
export const AI_PAGE_STATUSES = ["draft", "bound", "evaluated", "live"] as const;
export type AiPageStatus = (typeof AI_PAGE_STATUSES)[number];

/** Bậc số của status để so "chỉ đi lên" — một chỗ, không so chuỗi rải rác. */
export function aiPageStatusRank(status: AiPageStatus): number {
  return AI_PAGE_STATUSES.indexOf(status);
}

/**
 * Loại ô (slot) — tập ĐÓNG mà resolver của Người làm (0022) phải phủ hết: `member` → member-resolver
 * (0/1/≥2 ⇒ hỏi), `money` → bộ đổi tiền trả TẬP ứng viên, `fund`/`collection`/`group` → tra DB theo
 * scope, `date` → 'hôm qua/30-9', `enum` → `choices` của chính ô, `text`/`list` → chữ nguyên văn.
 * Thêm loại ở đây = thêm resolver ở 0022; test 0022 duyệt tập này nên thiếu resolver là đỏ.
 */
export const AI_SLOT_TYPES = ["member", "money", "fund", "collection", "group", "date", "enum", "text", "list"] as const;
export type AiSlotType = (typeof AI_SLOT_TYPES)[number];

/**
 * Vai nhóm mà một trang mở cho — `GROUP_ROLE_KEYS` (một nguồn trong domain, `satisfies` khoá theo `GROUP_ROLE_LABEL_VI`,
 * đã parity-lock với `GROUP_ROLE` của contracts) thay vì chép literal lần thứ ba hay cast `Object.keys` (#802 L4).
 */
export const AI_PAGE_GROUP_ROLES = GROUP_ROLE_KEYS;
export type AiPageGroupRole = (typeof AI_PAGE_GROUP_ROLES)[number];

/** `@UC0158`: mỗi trang ≥10 câu ví dụ (có dấu · không dấu · nói tắt) — câu ví dụ là SEED của bộ eval. */
export const AI_PAGE_MIN_EXAMPLES = 10;
/**
 * Sàn RIÊNG cho tier `redirect` (US-AIV2-0049, `@UC0216`). `USECASES-AIV2.md` nói trang redirect sinh tự động
 * *"chỉ có tên + vai + nút"* — nhưng 0 câu ví dụ là **trang có tên mà câm**: Người phiên dịch chọn trang theo
 * `llm.description` + ví dụ, nên trang không ví dụ nào chỉ tới được qua danh sách "cách dùng". Bài học
 * *"covered means reachable"* (MEMORY): một ô trong bảng phủ mà người dùng không gõ ra được là lời khai, không
 * phải năng lực. ⇒ sàn hạ xuống 3 (rẻ để viết) chứ KHÔNG xuống 0; luật "cả có dấu lẫn không dấu" giữ nguyên.
 * Muốn trang redirect được gọi nhiều hơn ⇒ nâng nó thành trang viết tay với ≥10 ví dụ (4 trang của 0049).
 */
export const AI_REDIRECT_MIN_EXAMPLES = 3;

/** Sàn ví dụ theo tier — một bảng, không if-else rải (validator và test cùng đọc). */
export function aiPageMinExamples(tier: AiPageTier): number {
  return tier === "redirect" ? AI_REDIRECT_MIN_EXAMPLES : AI_PAGE_MIN_EXAMPLES;
}

/**
 * Khoá BẢN ĐỒ của một cửa mobile: `POST /groups/{groupId}/members` — động từ HOA + đường dẫn có `{param}`.
 *
 * 🔴 KHÔNG mang tiền tố `/v1`: đó là của máy chủ (`setGlobalPrefix`), không phải của cửa. Lẫn vào thì cùng
 * một cửa có hai khoá và bản đồ đếm đôi. Khoá này phải khớp CHÍNH XÁC chuỗi mà bộ quét đọc ra từ khai báo
 * Retrofit của `apps/mobile` — cùng một chuỗi ở hai đầu mới so được.
 */
export const AI_ENDPOINT_RE = /^(GET|POST|PATCH|PUT|DELETE) \/(?!v\d+\/)[A-Za-z0-9\-_/{}]*$/;

/** Mã trang: `ONB-CREATE-GROUP`, `COL-EQUAL`, `PAY-REPORT` — chữ HOA, gạch nối (khớp `USECASES-AIV2.md`). */
export const AI_PAGE_ID_RE = /^[A-Z][A-Z0-9]*(?:-[A-Z0-9]+)*$/;
/** Trần độ dài id (review #805 M-c): `page`/`slot` trong Phiếu là chuỗi LLM sinh, nội suy vào cột không redact — regex không cap. */
export const AI_PAGE_ID_MAX_CHARS = 64;
export const AI_SLOT_ID_MAX_CHARS = 48;
/** Tên ô / tên chỗ trống trong template: `soTien`, `hanChot`, `ten` — camelCase, để `{soTien}` đọc được. */
export const AI_SLOT_ID_RE = /^[a-z][a-zA-Z0-9]*$/;

/** AI-COL-OPEN-PHRASING (@UC0195 @UC0220) — kiểu đợt thu mà một trang mở (`aiPageSchema.collectionFromSentence.cadence`) — tập ĐÓNG. */
export const AI_COLLECTION_CADENCES = ["once", "recurring", "deadline"] as const;
export type AiCollectionCadence = (typeof AI_COLLECTION_CADENCES)[number];
/** Executor: `groups.createGroup` — `<service>.<method>`; 0023 ràng nó về CÙNG hàm với controller GUI (`@UC0156`). */
export const AI_EXECUTOR_RE = /^[a-z][A-Za-z0-9]*\.[a-z][A-Za-z0-9]*$/;
/**
 * Route mobile cho tier `redirect`: `/groups/:groupId/settings`.
 *
 * 🔴 US-AIV2-0049 — SỬA MỘT LỖI CHƯA AI CHẠM: bản cũ `/^\/[a-z0-9\-\/:]*$/` chỉ cho chữ THƯỜNG, nên
 * chính ví dụ viết ngay trên nó (`:groupId` — có chữ `I` hoa) **không lọt qua chính nó**. Không ai thấy
 * vì tới 0049 mới có trang `redirect` thật đầu tiên; 19 trang chat không khai `screen` bao giờ.
 * Tên tham số của `go_router` là camelCase (`:groupId`, `:collectionId`, `:memberDueId`) ⇒ phải cho chữ hoa.
 */
export const AI_SCREEN_ROUTE_RE = /^\/[A-Za-z0-9\-_/:]*$/;
/**
 * Đáp án tiền của bộ eval (`evalAmounts`, US-AIV2-0110 S03): chuỗi minor DƯƠNG, không số 0 dẫn đầu, không dấu,
 * không phần lẻ — tiền trên wire LUÔN là chuỗi minor (CLAUDE.md §3.1). Số 0 không phải một đáp án tiền: câu không
 * nói số thì KHÔNG khai, đừng khai "0".
 */
export const AI_EVAL_AMOUNT_MINOR_RE = /^[1-9][0-9]*$/;
/** Cờ tính năng phải BẬT để trang sống: snake_case, khớp khoá `feature_flags`. */
export const AI_FLAG_KEY_RE = /^[a-z][a-z0-9_]*$/;

/**
 * 0023b (`@UC0226`, QĐ PO 17/09): ô quỹ/đợt thu/nhóm TỰ ĐIỀN khi người dùng không nhắc mà phạm vi có ĐÚNG MỘT mục đang hoạt động;
 * ≥2 ⇒ hỏi kèm lựa chọn; 0 ⇒ huỷ phiếu + dẫn sang `createPage`. Chỉ cho `fund`/`collection`/`group` (validator); ô `member` KHÔNG BAO GIỜ
 * tự điền (cam kết 4 của epic). Engine đọc field này — không hardcode loại ô (cam kết 6).
 */
/**
 * 🔴🔴 US-AIV2-0054b (@UC0176 · **QĐ PO 19/09/2026**, sau review #881 M-4) — Ô NÀY ĐƯỢC **ÁNH XẠ** VÀO TẬP ĐÓNG.
 *
 * §A-BIS cho ô `enum` đổi chốt từ *"giá trị phải có TRONG CÂU"* sang *"phải có TRONG `choices`"*. Nhưng lý lẽ của
 * QĐ là: *"tập đóng là chốt an toàn **vì model không bịa được giá trị ngoài catalog**"* — và điều đó chỉ đúng khi
 * **MỌI giá trị trong catalog đều vô hại**.
 *
 * | ô | catalog | *"không bịa ngoài catalog"* có còn là chốt? |
 * |---|---|---|
 * | `loaiNhom` (`ONB-CREATE-GROUP`, T1, **0 bút toán**) | 9 loại nhóm | ✅ chọn nhầm loại nhóm ⇒ sửa được, 0 đồng |
 * | `quyetDinh` (`PAY-CONFIRM-DECIDE`, T2) | **`["xác nhận", "từ chối"]`** | ❌ **KHÔNG** — chọn sai **TRONG** catalog đã đủ sai |
 * | `chuKy` (`COL-RECURRING`, **T2, BOUND, chạy hôm nay**) | `["hằng tháng","hằng quý"]` | ❌ tham số đẻ ra nghĩa vụ tiền định kỳ |
 *
 * Ca đo được (tái lập ở `enum-anchor.invariants.spec.ts`): câu **phủ định** *"chua thay tien cua Lan dau"* với span
 * `quyetDinh = "xác nhận"` — **luật cũ VỨT ⇒ HỎI**; nếu ánh xạ vô điều kiện thì **GIỮ "xác nhận"**, và sau bản vá
 * thẻ xác nhận của người là lưới **cuối cùng** còn lại.
 *
 * 🔴 US-AIV2-0112 H08(a) (QĐ PO 30/09 P9): `quyetDinh` VẪN KHÔNG ánh xạ — model vẫn không được viết "xác nhận" ngoài câu (`value_not_from_sentence`).
 * "tiền X về rồi" ⇒ "xác nhận" đến từ CODE (lưới khuôn ĐÓNG, nghĩa `money-received` của `AI_SLOT_FROM_SENTENCE`), không từ model.
 *
 * ⇒ Ánh xạ là **OPT-IN TỪNG Ô**, khai ở **Sổ** (không phải danh sách tay trong code). **Vắng ⇒ giữ neo-vào-câu** —
 * đúng luật repo *"default = TẮT ⇒ 0 REGRESS"*. Số đo trên 12 bản ghi đông lạnh: opt-in bật cho `loaiNhom` cho
 * **cùng safety 6** như áp-cho-mọi-ô-enum, tức **không đánh đổi**: nó chỉ gỡ phơi nhiễm của hai ô quyết định.
 *
 * 🔴 `judgeSpan` đòi **CẢ HAI** `type === "enum"` **VÀ** `anchor === "choices"` — hai khoá ĐỘC LẬP. Nhờ vế thứ nhất,
 * ô `money` không bao giờ vào nhánh phân loại **kể cả khi** ai đó lỡ khai `anchor` cho nó và validator bị gỡ.
 */
export const AI_SLOT_ANCHORS = ["choices"] as const;
/**
 * 🔴 US-AIV2-0112 H02 · review #1070 H2/M1 — NGHĨA của ô mở lưới đỡ suy lựa chọn từ câu (`aiSlotSchema.fromSentence`). Mỗi nghĩa có PHỦ QUYẾT
 * ngữ cảnh riêng ở `enum-fallback.ts` (`FROM_SENTENCE_VETOES`, vét cạn theo tập này):
 *  · `sole-frequency` — chu kỳ thu: câu đếm LẦN khác 1 trong kỳ ("mỗi tháng 2 lần") hoặc "một lần thôi" ⇒ không phải chu kỳ đó ⇒ hỏi;
 *  · `sole-group-type` — loại nhóm (KHÔNG đổi được sau khi tạo): ngữ cảnh người lớn/khoá học ("cựu", "đại học", "cao học", "người lớn",
 *    "khoá", mã khoá "K30") ⇒ hỏi (QĐ điều phối: thà hỏi còn hơn chọn sai thứ không đổi được).
 *  · `money-received` (US-AIV2-0112 H08(a), QĐ PO 30/09 P9) — nghĩa KHẲNG ĐỊNH của ô quyết định lời báo: câu theo khuôn ĐÓNG "tiền của X đã về"
 *    (`parseMoneyArrived`) ⇒ lựa chọn DUY NHẤT được chốt ("xác nhận"). Nghĩa DUY NHẤT được đi cùng `guardedChoices` (`AI_SLOT_FROM_SENTENCE_AFFIRMING`);
 *    lưới riêng (`fillReceivedDecisionFromSentence`) còn đòi Phiếu gạch ô `askAfter` (người báo) đúng chỗ người trả. Đảo QĐ C2 19/09 đúng cho ô này.
 */
export const AI_SLOT_FROM_SENTENCE = ["sole-frequency", "sole-group-type", "money-received"] as const;
export type AiSlotFromSentence = (typeof AI_SLOT_FROM_SENTENCE)[number];
/**
 * 🔴 H08(a) — nghĩa KHẲNG ĐỊNH: được (và PHẢI) khai cùng `guardedChoices` có ĐÚNG MỘT phần tử + `askAfter` trỏ ô `member` (validator). Mọi nghĩa khác
 * vẫn cấm đi cùng `guardedChoices` (`SLOT_FROM_SENTENCE_GUARDED`). Tập ĐÓNG — thêm phần tử = một QĐ PO mới.
 */
export const AI_SLOT_FROM_SENTENCE_AFFIRMING: readonly AiSlotFromSentence[] = ["money-received"];
export type AiSlotAnchor = (typeof AI_SLOT_ANCHORS)[number];
/** Loại ô được phép khai `anchor` — tập đóng, validator đo (giống `AI_SLOT_AUTOFILL_TYPES`). */
export const AI_SLOT_ANCHOR_TYPES: readonly AiSlotType[] = ["enum"];

export const AI_SLOT_AUTOFILL = ["sole-active"] as const;
export type AiSlotAutofill = (typeof AI_SLOT_AUTOFILL)[number];
/** Loại ô được phép khai `autofill` — tập đóng, validator đo. */
export const AI_SLOT_AUTOFILL_TYPES: readonly AiSlotType[] = ["fund", "collection", "group"];
/**
 * Loại ô mà PHẠM VI phụ thuộc NHÓM hiệu lực (resolver không nhóm ⇒ dữ liệu MỌI nhóm / rỗng) — bảng VÉT CẠN theo `AiSlotType` (review #825 r2
 * LOW-3): thêm loại ô mới mà không khai ở đây ⇒ tsc đỏ, không lặng lẽ mở lại lỗ "resolve dưới nhóm chưa chốt" (H1).
 */
export const AI_SLOT_TYPE_NEEDS_GROUP: Readonly<Record<AiSlotType, boolean>> = Object.freeze({
  member: true,
  money: false,
  fund: true,
  collection: true,
  group: false,
  date: false,
  enum: false,
  text: false,
  list: false,
});

/**
 * Người làm hỏi resolver cho ô này KỂ CẢ khi người dùng chưa nói gì về nó (bắt buộc ⇒ phải có giá trị; `autofill` ⇒ thử tự điền).
 * MỘT vị-từ cho engine (`resolveAll`) VÀ cổng (đường vào của ô `group` ở phiên chưa có nhóm) — review #825 LOW-1: hai bản chép lệch nhau ⇒
 * cổng mở phiếu cho một ô nhóm mà engine không bao giờ hỏi ⇒ phiếu kẹt.
 */
export function aiSlotAutoResolves(slot: { readonly required: boolean; readonly autofill?: AiSlotAutofill }): boolean {
  return slot.required || slot.autofill !== undefined;
}

/**
 * 🔴 US-AIV2-0033 (@UC0194) — ô `list` xử lý mục LẶP thế nào. Mặc định `drop` = hành vi từ 0022 ⇒ **0 regress**
 * (luật repo: *ngưỡng mặc định = 0 ⇒ TẮT ⇒ 0 REGRESS*). `ask` chỉ dành cho ô nghĩa "TÊN MỚI để TẠO", nơi gộp một
 * mục trùng nghĩa là **xoá mất một người thật** (`ONB-ADD-MEMBERS` → `createSeats` → `member_dues`).
 */
export const AI_SLOT_DUPLICATES = ["drop", "ask"] as const;
export type AiSlotDuplicates = (typeof AI_SLOT_DUPLICATES)[number];

/**
 * 🔴 US-AIV2-0121 (@UC0390) — ô `list` nhận TỆP danh sách (cửa `POST /v1/ai/v2/tasks/:taskId/file`). Danh sách ĐÓNG: mỗi loại là MỘT bộ đọc
 * tất định (`member_names` ⇒ `parseMemberFile`, CSV/TXT). Thêm loại = thêm bộ đọc, không phải thêm chữ vào đây.
 */
export const AI_SLOT_FILE_IMPORT_KINDS = ["member_names"] as const;
export type AiSlotFileImportKind = (typeof AI_SLOT_FILE_IMPORT_KINDS)[number];

/**
 * 🔴 US-AIV2-0109 S13 (@UC0248) — PHẠM VI NGƯỜI của một ô `member`. Vắng ⇒ `obligation` ("ai phải đóng" — luật cũ của mọi ô).
 * `payer` = "ai được ứng tiền" (`buildPayerCandidateWhere`, QĐ PO 5b). Chip của ô và phép kiểm của executor đọc CÙNG luật
 * (`memberRefScopeWhere`), nên ô "Ai ứng tiền?" không còn mời chọn một suất mà phép kiểm từ chối (E-6).
 * Chỉ cho `type: "member"` (validator).
 * 🔴 US-AIV2-tourguide-0010 (@UC0373) — `represented` = suất nghĩa vụ CHÍNH người hỏi đại diện (ô "Bạn báo cho bé nào?" của `PAY-REPORT`);
 * phụ thuộc người hỏi ⇒ tầng api ghép với `actorRepresentedLiveSeatWhere` (không qua `memberRefScopeWhere`). Tập này TRÙNG `MEMBER_REF_SCOPE`
 * (`obligation-scope.test.ts` khoá) — thêm giá trị là sửa CẢ HAI + resolver.
 */
export const AI_SLOT_MEMBER_SCOPES = ["obligation", "payer", "represented"] as const;
/**
 * 🔴 US-AIV2-0113 M01 (@UC0192) — HÌNH của chữ ở ô `text`. `name` = ô dùng làm TÊN (nhóm…): resolver bỏ chữ dẫn đặt tên ("tên/tên là/gọi là…"), cắt đuôi
 * danh sách (", gồm …"), và ở lượt MỞ coi chữ bắt đầu bằng "nhóm" (không chữ dẫn) là lời mô tả ⇒ hỏi tên — `resolveNameText`. Vắng ⇒ nguyên văn (0 regress).
 * `note` (UX-SMALL-WORDING V-5 · @UC0197) = ô LỜI NHẮN gửi người khác (ghi chú cho thủ quỹ · lý do từ chối): bỏ lời đệm cuối câu ("…rồi nha") — `resolveNoteText`.
 */
export const AI_SLOT_TEXT_FORMS = ["name", "note"] as const;
export type AiSlotMemberScope = (typeof AI_SLOT_MEMBER_SCOPES)[number];

/**
 * 🔴 US-AIV2-0114 L07 (@UC0219) — ô `date` là MỘT ĐẦU của KHOẢNG ngày (`from` = ngày đầu · `to` = ngày cuối). Khai ⇒ bộ đọc ngày (`resolveDateSlot`)
 * hiểu thêm cụm KHOẢNG ("tháng này", "cả năm", "tháng trước", "quý này", "năm ngoái", "tháng 9") và trả đúng ĐẦU của khoảng; trang khai đủ cặp thì
 * lưới khoảng (`fillDateRangeFromSentence`) được đưa chữ của câu cho cả hai đầu khi Phiếu không gạch ô ngày nào. Validator: chỉ ô `date`, trang khai
 * thì khai ĐÚNG MỘT `from` + ĐÚNG MỘT `to`. Không vào prompt ⇒ không đổi bản ghi eval.
 */
export const AI_SLOT_RANGE_EDGES = ["from", "to"] as const;
export type AiSlotRangeEdge = (typeof AI_SLOT_RANGE_EDGES)[number];

export const aiSlotSchema = z
  .object({
    id: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE),
    type: z.enum(AI_SLOT_TYPES),
    required: z.boolean(),
    /** Câu hỏi khi ô còn thiếu — template có chỗ trống, render ở 0023 (số/tên chèn SAU LLM, `@UC0154`). */
    ask: z.string().min(1),
    /** Chỉ cho `type: "enum"`: tập giá trị đóng (≥2). Ô loại khác khai `choices` ⇒ validator đỏ. */
    choices: z.array(z.string().min(1)).min(2).readonly().optional(),
    /**
     * 0054b (QĐ PO 19/09): ô này được **ÁNH XẠ** vào `choices` thay vì phải trích nguyên văn từ câu — xem
     * `AI_SLOT_ANCHORS`. **Vắng ⇒ giữ neo-vào-câu.** Chỉ cho `type: "enum"` (validator); khai ở loại ô khác ⇒ đỏ.
     */
    anchor: z.enum(AI_SLOT_ANCHORS).optional(),
    /**
     * 🔴 US-AIV2-0111 C10 (@UC0205 @UC0206 @UC0207) — chỉ cho `type: "enum"`: CÁCH NGƯỜI VIỆT NÓI mỗi lựa chọn, khoá = một phần
     * tử của `choices`. `resolveEnumSlot` dùng nó SAU khi chữ không khớp chính lựa chọn nào: cụm của người dùng chứa trọn một cụm
     * đồng nghĩa (theo TỪ, bỏ dấu) ⇒ lựa chọn đó; nhiều lựa chọn khớp ⇒ cụm DÀI NHẤT thắng, hoà ⇒ hỏi giữa đúng các lựa chọn hoà.
     * Đo 28/09: LLM trích ĐÚNG cụm người nói (`"còn bao nhiêu tiền"`) vào ô `chiSo`, nhưng không cụm nào chứa chữ của một lựa
     * chọn ⇒ trợ lý hỏi lại câu hỏi quốc dân. Đồng nghĩa KHÔNG vào prompt (không đổi thứ model thấy — bản ghi eval không mục)
     * và KHÔNG nới phép neo: cụm vẫn phải có TRONG CÂU (`judgeSpan`), đồng nghĩa chỉ dịch cụm đó sang một giá trị của Sổ.
     */
    synonyms: z.record(z.string().min(1), z.array(z.string().min(1)).min(1).readonly()).optional(),
    /**
     * 🔴 US-AIV2-0112 lô Sổ 2 (review #1036 HIGH-1/2 · @UC0199 · §3.7) — chỉ cho `type: "enum"` ở trang GHI (validator): các lựa chọn KHẲNG ĐỊNH
     * làm tiền/quyền đi tiếp ("xác nhận" lời báo · "duyệt" khoản chi · "đồng ý" đóng hộ). Câu/chữ mang PHỦ ĐỊNH hoặc NGHI VẤN ("chưa nhận
     * được", "nhận được chưa", "đừng duyệt", "hình như đã nhận", "?") ⇒ `resolveEnumSlot` KHÔNG BAO GIỜ chọn các lựa chọn này (hỏi lại), và
     * phép neo đưa CẢ CÂU cho resolver (LLM hay gạch span ngắn "nhận được", để "chưa" nằm ngoài). Vắng ⇒ không chốt (0 regress trang đọc).
     */
    guardedChoices: z.array(z.string().min(1)).min(1).readonly().optional(),
    /**
     * 🔴 US-AIV2-0112 H02 (@UC0220 @UC0192) — chỉ cho `type: "enum"` (validator): OPT-IN của LƯỚI ĐỠ Ô LỰA CHỌN
     * (`fillEnumSlotsFromSentence`). Đo 28/09 (`ai_audit_log`, 4/4 lượt): *"hàng tháng mỗi người đóng quỹ đội 100k"* ⇒ Người phiên
     * dịch chỉ gạch `soTien`, KHÔNG gạch `chuKy` ⇒ trợ lý hỏi lại "Thu theo chu kỳ nào?" dù người dùng vừa nói. Khai ô này ⇒ Người làm
     * được tìm lựa chọn trong CẢ CÂU (nhãn hoặc `synonyms`, theo TỪ liền mạch) khi Phiếu không gạch ô đó.
     *
     * Giá trị là LỜI KHAI VỀ NGHĨA của ô (`AI_SLOT_FROM_SENTENCE`): câu nói ra ĐÚNG MỘT lựa chọn (nhãn/đồng nghĩa) thì lựa chọn đó CHẮC CHẮN là
     * giá trị của ô — TRỪ các ngữ cảnh mà nghĩa đó biết là lừa (phủ quyết theo nghĩa, `enum-fallback.ts`; review #1070 H2/M1). Kèm theo,
     * ô khai cờ này có CHỐT PHỦ ĐỊNH/NGHI VẤN ở MỌI nhánh (lưới câu · span nhãn · span đồng nghĩa · lượt đầu · lượt trả lời): câu mang
     * dấu hiệu phủ định/nghi vấn ⇒ HỎI, không chọn (`resolveEnumSlot`, `judgeSpan`). Vắng ⇒ không lưới, không chốt thêm (0 regress).
     * ⚠️ KHÔNG vào prompt, KHÔNG vào túi chữ tìm trang ⇒ không làm mục bản ghi eval.
     */
    fromSentence: z.enum(AI_SLOT_FROM_SENTENCE).optional(),
    /**
     * 🔴 US-AIV2-0111 C10 — chỉ cho `type: "enum"` ở trang `read` (validator): lựa chọn GIÀU THÔNG TIN NHẤT, dùng khi người dùng
     * không nói chỉ số nào (ô trống) hoặc nói một cụm không khớp gì — thay vì hỏi lại. Mơ hồ giữa ≥2 lựa chọn thì VẪN hỏi.
     * Bộ phân phối (`resolveSlot`) chỉ bật nó khi `pageTier === "read"`: trang ghi đoán hộ một lựa chọn là đoán hộ một tham số
     * có thể đẻ ra tiền (`COL-RECURRING.chuKy`). Vắng ⇒ hỏi như cũ (0 regress).
     */
    fallback: z.string().min(1).optional(),
    /** 0023b: tự điền khi duy nhất — xem `AI_SLOT_AUTOFILL`. Có `autofill` ⇒ phải có `createPage` + `emptyAsk` (validator). */
    autofill: z.enum(AI_SLOT_AUTOFILL).optional(),
    /** Trang tạo mục còn thiếu khi phạm vi RỖNG (id ∈ Sổ — validator liên-trang). */
    createPage: z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE).optional(),
    /** Câu nói khi phạm vi rỗng — đi kèm chip `run_page createPage`; không phải `ask` (ask = "cái nào?"). */
    emptyAsk: z.string().min(1).optional(),
    /**
     * 🔴 US-AIV2-0109 S06 (@UC0243) — câu HỎI LẠI khi ô NÀY mơ hồ (≥2 ứng viên). Thuộc về Ô, không thuộc về trang: câu cấp trang cũ
     * (`templates.clarify`) viết cho MỘT ô nhưng được dùng cho MỌI ô ⇒ E2E 25/09 thấy ô "quyết định" bị hỏi "lời báo của ai?".
     * Vắng ⇒ ô enum dùng chính `ask` (câu hỏi lựa chọn), ô khác dùng câu mặc định.
     */
    clarify: z.string().min(1).optional(),
    /** Chỉ cho `type: "list"` (0033): mục lặp thì gộp (`drop`, mặc định) hay HỎI (`ask`). Khai ở loại ô khác ⇒ validator đỏ. */
    /** Chỉ cho `type: "member"` (0109 S13): phạm vi người của ô — xem `AI_SLOT_MEMBER_SCOPES`. Vắng ⇒ `obligation`. */
    memberScope: z.enum(AI_SLOT_MEMBER_SCOPES).optional(),
    /** Chỉ cho `type: "date"` (US-AIV2-0114 L07): ô là đầu nào của khoảng ngày — xem `AI_SLOT_RANGE_EDGES`. Vắng ⇒ ô ngày ĐƠN (luật M02). */
    rangeEdge: z.enum(AI_SLOT_RANGE_EDGES).optional(),
    duplicates: z.enum(AI_SLOT_DUPLICATES).optional(),
    /** Câu hỏi khi danh sách có mục lặp — bắt buộc khi `duplicates: "ask"`; không phải `ask` (ask = "dán danh sách đi"). */
    duplicateAsk: z.string().min(1).optional(),
    /**
     * Chỉ cho `type: "list"` (0033 vòng 2): câu hỏi khi span của LLM cho ÍT mục hơn câu người dùng vừa dán
     * (**trích xuất MỘT PHẦN**). Bắt buộc với ô `list` mang `duplicates: "ask"` — cùng một triết lý: thà hỏi còn hơn ghi thiếu.
     */
    partialAsk: z.string().min(1).optional(),
    /**
     * 🔴 US-AIV2-0111 C06 re-review #1018 HIGH (@UC0320) — ô này KHÔNG được hỏi/nhận chữ LLM gạch TRƯỚC khi ô `askAfter` đã rõ.
     *
     * Đo 29/09 (probe `tuan`, câu HỎI *"co ai bao da chuyen tien chua"* ⇒ PAY-CONFIRM-DECIDE): LLM gạch ô `quyetDinh` từ chính câu hỏi
     * ⇒ Người làm hỏi *"Xác nhận đã nhận, hay từ chối?"* kèm nút «xác nhận» TRƯỚC khi nói ai đang có lời báo (2/3 lượt). Máy trạng thái
     * hỏi ô MƠ HỒ trước ô THIẾU — đúng cho hầu hết trang, sai cho trang mà một quyết định chỉ có nghĩa sau khi biết nó áp cho ai.
     * Luật (Người làm, chỉ trang khai): ô `askAfter` chưa rõ mà ô này mang chữ CHƯA phân giải được ⇒ bỏ chữ đó, hỏi ô kia trước. Giá trị
     * ĐÃ rõ (người dùng nói thẳng "xác nhận…") giữ nguyên. Không vào prompt ⇒ không đổi bản ghi eval. Validator: trỏ ô có thật, không vòng.
     */
    askAfter: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE).optional(),
    /**
     * 🔴 US-AIV2-0111 C06 re-review #1018 HIGH (@UC0320) — câu ĐỌC khi binding nói ô bắt buộc này có **0 giá trị hợp lệ theo trạng thái
     * trang** (`slotCandidates` rỗng — vd 0 lời báo chờ) và người dùng chưa nói gì cho nó ⇒ Người làm đóng phiếu và trả câu này, thay vì
     * hỏi "Lời báo của ai?" kèm mỗi nút Huỷ (ngõ cụt đo 29/09). Khác `emptyAsk` (phạm vi RESOLVER rỗng + nút tạo, chỉ ô autofill).
     * Chỉ ô bắt buộc (ô tuỳ chọn rỗng thì bỏ trống, không kết thúc việc).
     */
    emptyByState: z.string().min(1).optional(),
    /**
     * 🔴 US-AIV2-0113 M01 (@UC0192) — chỉ cho `type: "text"` (validator): xem `AI_SLOT_TEXT_FORMS`. KHÔNG vào prompt, KHÔNG vào túi chữ tìm trang ⇒
     * không làm mục bản ghi eval (đo: `collect-ai-eval-recordings --only-stale --dry`).
     */
    textForm: z.enum(AI_SLOT_TEXT_FORMS).optional(),
    /**
     * 🔴 US-AIV2-0121 (@UC0390) — chỉ cho `type: "list"` (validator): ô nhận cả TỆP danh sách. Cửa tệp điền ô TẤT ĐỊNH (0 LLM) qua đúng
     * đường của chip `choose` (`fillSlots`) ⇒ hạ nguồn y như chữ dán (hỏi tên lặp · trần · thẻ xác nhận · `confirm()` gác ×2).
     * `ask` = câu hỏi NGƯỜI DÙNG THẤY khi ô có thể nhận tệp (thay `slot.ask` ở nhánh hỏi thường — `askVariant`). Cố ý KHÔNG sửa `slot.ask`:
     * nó vào prompt của Người phiên dịch (`prompt.ts`) ⇒ sửa = mục `promptHash` của mọi ca eval truy hồi trúng trang. Không vào prompt.
     */
    fileImport: z.object({ kind: z.enum(AI_SLOT_FILE_IMPORT_KINDS), ask: z.string().min(1) }).strict().optional(),
    /**
     * 🔴 UX-COLLECTION-PERIOD (@UC0220 · nghiệm thu UX-H3 15/10) — câu hỏi NGƯỜI DÙNG THẤY ở nhánh hỏi thường (thay `ask` — `askVariant` của Người làm),
     * khi `ask` (NGHĨA của ô trong prompt — `prompt.ts`) không đủ nói người dùng đang ở VIỆC nào. Đo: *"thu quy thang 11 moi nguoi 150k"* ⇒ COL-RECURRING hỏi
     * «Bắt đầu thu từ ngày nào?» — không một chữ nói đây là LỊCH thu định kỳ. Cố ý KHÔNG sửa `ask`: sửa = mục `promptHash` của mọi ca eval truy hồi trúng
     * trang (như `fileImport.ask`). Chỗ trống chỉ được là ô của trang (validator); thiếu nhãn lúc hỏi ⇒ Người làm dùng `ask` (không bao giờ ném giữa lượt).
     * Không vào prompt, không vào túi chữ tìm trang.
     */
    shownAsk: z.string().min(1).optional(),
    /**
     * 🔴 UX-POB-WORDING (@UC0218 · nghiệm thu GUIDE-UX2 V-2) — chỉ cho `type: "enum"` (validator): câu NGƯỜI DÙNG THẤY khi lựa chọn ĐANG MỜI của ô
     * (resolver ∩ trạng thái trang — `slotCandidates` của binding, `offerSlotsFromBinding`) là ĐÚNG tập `choices` của mục. Đo 16/10: phụ huynh bấm nút
     * thẻ «Trả lời đề nghị đóng hộ» ⇒ binding đã thu ô `quyetDinh` về hai lựa chọn trả lời, nhưng câu hỏi vẫn là `ask` chung của trang hai nhánh
     * «Bạn muốn đóng hộ cho ai, hay trả lời…». Mục đầu tiên render được thắng (chỗ trống chỉ là Ô, như `shownAsk`; ô thiếu nhãn ⇒ mục sau, hết mục ⇒
     * `shownAsk`/`ask`). Binding vẫn KHÔNG đặt câu (`binding-slot-offer.ts`) — câu ở Sổ, binding chỉ nói tập. Không vào prompt (`ask` của ô enum cũng
     * không vào), không vào túi chữ tìm trang ⇒ không làm mục bản ghi eval.
     */
    offeredAsks: z
      .array(z.object({ choices: z.array(z.string().min(1)).min(1).readonly(), ask: z.string().min(1) }).strict())
      .min(1)
      .readonly()
      .optional(),
    /**
     * 🔴 AI-MENU-POB-ENTRY (@UC0218 · Jira GOPPY-45) — câu NGƯỜI DÙNG THẤY theo NHÁNH: ô enum `slot` (một ô KHÁC của cùng trang — validator) đã CHỐT một
     * giá trị thuộc `choices` của mục ⇒ câu của mục thay `shownAsk`/`ask` khi hỏi ô NÀY. Đo 28/10: `PAY-ON-BEHALF` vừa chọn «đóng hộ» mà ô người vẫn hỏi
     * «Đóng hộ cho ai? (hoặc: ai đề nghị đóng hộ bạn?)» — vế sau là câu của nhánh trả lời. Mục đầu khớp + render được thắng; chỗ trống chỉ là Ô (luật
     * `shownAsk`); `offeredAsks` của chính ô vẫn thắng. Cố ý KHÔNG sửa `ask` (nó vào prompt ⇒ mục `promptHash` eval); không vào túi chữ tìm trang.
     */
    branchAsks: z
      .array(
        z
          .object({
            slot: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE),
            choices: z.array(z.string().min(1)).min(1).readonly(),
            ask: z.string().min(1),
          })
          .strict(),
      )
      .min(1)
      .readonly()
      .optional(),
  })
  .strict();
export type AiSlot = z.infer<typeof aiSlotSchema>;

/**
 * Template theo tier — chỗ trống `{ten}`, `{soTien}` chèn sau LLM. Cái nào BẮT BUỘC do tier quyết
 * (`validate.ts`): `T1`/`T2` cần `confirm` + `done` · `read` cần `reply` · `redirect` cần `redirect`.
 */
/**
 * 🔴 US-AIV2-0034 (review #832 HIGH-1) — KHOÁ DỰ TRỮ "executor KHÔNG ghi gì".
 *
 * Có cửa ghi mà một lượt hợp lệ vẫn **không sinh dòng nào**: `AdhocCollectionService.createAdhoc` gặp khoá idempotency đã dùng thì
 * REPLAY — trả nguyên kết quả lần trước, 0 đợt thu mới, 0 `member_dues` mới. Vì facts của lần trước BẰNG facts vừa preview (khoá ký
 * trên chính những con số đó), câu `done` render ra **giống hệt** lượt ghi thật: *"Đã mở đợt thu… Chưa ai đóng"*. Sổ tiền biết
 * (`audit_log.diff.replay = true`), người dùng thì không — và nhóm mở đợt thu thứ hai trong tháng tin rằng cả lớp vừa nhận nghĩa vụ
 * trong khi **không ai nợ một đồng**.
 *
 * Nên: executor trả khoá này (giá trị `"true"`) ⇒ `renderDone` dùng `templates.doneAlready` thay cho `templates.done`. Khoá bắt đầu
 * bằng `_` nên `AI_SLOT_ID_RE` cấm — nó KHÔNG BAO GIỜ thành một chỗ trống, không lọt vào câu chữ, và `AiDoerService` chỉ so facts
 * theo các khoá đã `preview` nên nó cũng không bị coi là "lệch phiếu".
 */
export const AI_FACT_NO_WRITE_KEY = "__noWrite";

/**
 * 🔴 US-AIV2-0066 (@UC0219) — KHOÁ DỰ TRỮ "cảnh báo trên phiếu".
 *
 * Binding tính cảnh báo trong `preview` (nơi nó đã đọc sẵn số dư/đợt thu) và trả qua khoá này; renderer
 * đưa vào `AiTurn.warning` — **một dòng**, không phải một chỗ trống của template. Nhờ vậy thêm cảnh báo
 * cho một trang KHÔNG bắt trang đó sửa `templates.confirm`, và **không trang nào** phải khai một nhãn mới.
 *
 * 🔴 VÌ SAO PHẢI LÀ KHOÁ `_`-PREFIX, không phải một fact thường: `AiDoerService` so **từng khoá** của
 * facts preview với facts execute để chống "ký phiếu này, ghi bản kia". Nhưng cảnh báo đọc **số dư quỹ**,
 * mà lúc execute tiền đã ghi xong ⇒ số dư đổi ⇒ chuỗi cảnh báo đổi ⇒ phép so sẽ **rollback một bút toán
 * hợp lệ**. Khoá `_`-prefix được loại khỏi phép so (xem `isReservedFactKey`), nên chốt chống lệch phiếu
 * vẫn nguyên vẹn cho mọi con số THẬT của phiếu.
 */
export const AI_FACT_WARNING_KEY = "__warning";

/**
 * 🔴🔴 US-AIV2-tourguide-0005 (@UC0364 · TG-INV-3/4) — KHOÁ DỰ TRỮ "KẾT QUẢ của lượt này" (tên một kết quả đã khai ở `journey.outcomes`).
 *
 * Thay HAI khoá opt-in của S30 (`__guideCovered` "câu trả lời đã nói thẻ <id>" · `__guideCtaBlocked` "đừng mời nút tới trang <id>"): chặn là
 * opt-in thì mặc định HIỆN (N3 — 2/54 trang từng khai, 0/18 trang ghi). Nay binding TRẢ TÊN KẾT QUẢ (một tập ĐÓNG mỗi trang, khai ở Sổ), và
 * Sổ nói kết quả đó dẫn đi đâu (`next`) + đã nói thẻ nào (`covers`). Binding đọc dựng bằng `aiOutcome("<tên>")` (`next-steps.ts`) — một hình
 * để lưới tự khám phá đếm được. Trang GHI không phải khai: `done`/`done_already` suy từ `__noWrite` (cùng khoá `renderDone` đọc).
 * Vắng/tên lạ ở trang đọc ⇒ **0 nút + log** (fail-closed, `resolvePageOutcome`) — không còn lưới đỡ `journey.after` nào để rơi về.
 */
export const AI_FACT_OUTCOME_KEY = "__outcome";

/**
 * 🔴 US-AIV2-0113 ADVANCE-SHORTFALL (@UC0358 @UC0202 · QĐ PO 30/09 B) — KHOÁ DỰ TRỮ "mời nút Có người ứng tiền trước" trên phiếu chi QUỸ
 * TRẢ. Binding đặt `"true"` khi CHÍNH luật của chốt (`fundAdvanceSplit` ⇐ `checkGeneralSpend`) nói "thiếu, ứng bù được" VÀ người xem thấy
 * được số của quỹ (số bị che ⇒ KHÔNG đặt: một cái nút cũng là lời khai "thiếu"). Người làm đọc khoá này để mời nút sang `EXP-F-ADVANCE`;
 * nó KHÔNG so tiền lần hai. Không vào template, không vào phép so preview↔execute (`isReservedFactKey`). Không phải cổng.
 */
export const AI_FACT_ADVANCE_INVITE_KEY = "__advanceInvite";

/**
 * 🔴 US-AIV2-0113 M03 (@UC0213) — KHOÁ DỰ TRỮ "mở màn GUI của CHÍNH trang này" (`page.guiScreen`) ở lượt vừa chạy xong.
 *
 * Ca thật (E2E 28/09, `linh`/`tu`): *"cho xin stk để ck tiền quỹ"* ⇒ số TK, nhưng không số tiền, không nội dung CK, không QR. Màn QR
 * cần biết KHOẢN nào (nội dung CK do server sinh cho đúng khoản, có kiểm chủ khoản) ⇒ route có tham số ngoài `:groupId`. Chỉ BINDING
 * biết khoản đó (lấy từ "khoản của tôi" — phép cấp quyền, KHÔNG từ chữ LLM), nên nó KHAI bằng khoá này; tầng ghép (`apps/api`) dựng
 * nút bằng `guiScreenChip` ⇒ `fillScreenRoute` (tham số thiếu/rác ⇒ ném như mọi route Sổ). Route vẫn là của SỔ — binding chỉ đưa
 * GIÁ TRỊ tham số + nhãn nút, không bao giờ một đường dẫn.
 *
 * Giá trị: chuỗi dạng query (`label=…&memberDueId=…`, mã hoá bằng `URLSearchParams`). Vắng khoá / trang không khai `guiScreen` ⇒ không
 * nút (0 hồi quy). Không vào prompt, không vào chỉ mục tìm trang ⇒ 0 ca eval đổi.
 */
export const AI_FACT_GUI_SCREEN_KEY = "__guiScreen";

/**
 * 🔴 US-AIV2-tourguide-0013 (@UC0377 · K04 · TG-INV-3) — KHOÁ DỰ TRỮ "câu của trang HỨA một nút" ("bấm “X” bên dưới").
 *
 * Câu trang dựng TRƯỚC khi biết nút cuối của lượt: nút `next` của kết quả qua cổng + `ready()`, rồi dòng dẫn đường THAY bãi nút bằng một nút thẻ.
 * Binding vì thế đặt fact câu = bản KHÔNG hứa nút (an toàn mặc định) và khai bản CÓ nút bằng khoá này; tầng ghép (`apps/api` — `keepButtonPromise`)
 * dùng bản có nút CHỈ KHI nút của trang đó có trên lượt đã ghép xong (vị-từ `failureButtonShown`), chèn ĐÚNG nhãn của nút ấy. Giá trị: chuỗi dạng
 * query (`page=…&fact=…&withButton=…`, `URLSearchParams`). Không vào prompt, không vào chỉ mục tìm trang ⇒ 0 ca eval đổi.
 */
export const AI_FACT_BUTTON_PROMISE_KEY = "__buttonPromise";

/**
 * 🔴 UX-SMALL-WORDING (V-5 · @UC0322 @UC0364) — KHOÁ DỰ TRỮ "câu trả lời của lượt này nói về ĐỐI TƯỢNG nào" (cùng dạng `encodeReadySubject` của nút thẻ:
 * `collection:<id>`). Tầng ghép (`attachGuideLine` `subject`) chỉ coi thẻ là ĐÃ NÓI (`covers`) khi nút thẻ trỏ CÙNG đối tượng: câu tiến độ đợt A không phủ
 * thẻ tiến độ đợt B. Vắng ⇒ luật `covers` như cũ. Không vào template, không vào phép so preview↔execute (`isReservedFactKey`).
 */
export const AI_FACT_SUBJECT_KEY = "__subject";

/**
 * Khoá facts **DỰ TRỮ** — mang tín hiệu cho renderer, không phải một con số đã hứa với người dùng.
 *
 * Luật thay cho danh sách tên: `AI_SLOT_ID_RE` cấm `_` mở đầu nên không khoá nào kiểu này lọt được vào
 * câu chữ, và thêm một khoá dự trữ mới **không** phải nhớ cập nhật phép so ở `AiDoerService`.
 */
/**
 * 🔴 rà #914 MED-3 — TẬP ĐÓNG các khoá dự trữ, dùng ở CHỖ GHI RA (`AiDoerService`).
 *
 * `isReservedFactKey` trả lời *"khoá này có được miễn trừ khỏi phép so không"*; tập này trả lời
 * *"khoá nào được phép tồn tại"*. Hai câu khác nhau: câu đầu là một **hình dạng tên**, câu sau là
 * một **danh sách có chủ**. Một bất biến quét mã nguồn chỉ đóng được khoá viết thẳng — khoá tính
 * động (`["_","_soTien"].join("")`) lọt qua mọi regex.
 */
export const AI_RESERVED_FACT_KEYS: ReadonlySet<string> = new Set([
  AI_FACT_NO_WRITE_KEY,
  AI_FACT_WARNING_KEY,
  AI_FACT_OUTCOME_KEY,
  AI_FACT_GUI_SCREEN_KEY,
  AI_FACT_ADVANCE_INVITE_KEY,
  AI_FACT_BUTTON_PROMISE_KEY,
  AI_FACT_SUBJECT_KEY,
  "__groupId",
  "__fundId",
]);

export function isReservedFactKey(key: string): boolean {
  /*
   * 🔴 HAI gạch, không phải một (rà #914 MED-1). Cả bốn hằng dự trữ đều `__*`
   * (`__warning` · `__noWrite` · `__groupId` · `__fundId`), và repo đã tự viết `startsWith("__")` ở
   * `debt-remind.binding.spec.ts`. Một gạch nới miễn trừ rộng hơn thứ đang dùng — mà miễn trừ này
   * gỡ một con số khỏi chốt chống "ký phiếu này ghi bản kia", nên **rộng hơn cần là rộng hơn an
   * toàn**: `_soTien` sẽ lặng lẽ thoát phép so.
   */
  return key.startsWith("__");
}

export const aiPageTemplatesSchema = z
  .object({
    confirm: z.string().min(1).optional(),
    done: z.string().min(1).optional(),
    /**
     * Câu cho lượt mà executor **không ghi gì** vì thứ người dùng yêu cầu ĐÃ CÓ (xem `AI_FACT_NO_WRITE_KEY`).
     * Bắt buộc với trang nào có executor trả khoá đó — thiếu ⇒ `renderDone` NÉM, cố ý không có đường rơi về `done`
     * (rơi về `done` là quay lại đúng lời nói dối này).
     */
    doneAlready: z.string().min(1).optional(),
    reply: z.string().min(1).optional(),
    redirect: z.string().min(1).optional(),
    // `clarify` cấp trang ĐÃ GỠ (US-AIV2-0109 S06) — câu hỏi lại nay khai ở từng Ô (`aiSlotSchema.clarify`).
  })
  .strict();
export type AiPageTemplates = z.infer<typeof aiPageTemplatesSchema>;

/**
 * 🔴 review #1134 MED (@UC0320 @UC0176) — MỘT mục đáp án phụ là một QUYẾT ĐỊNH NỘI DUNG, không phải một lần trượt được đổi tên: mang
 * `reason` (vì sao trang kia cũng đúng) · `decision` (ai quyết, nguồn — QĐ PO/điều phối, AC) · `decidedOn` (YYYY-MM-DD). Cùng hình
 * `AI_EVAL_PAGE_OVERRIDES` (`eval/thresholds.ts`). `.strict()`: trường không ai đọc không có chỗ.
 */
export const aiEvalAlsoAcceptEntrySchema = z
  .object({
    pages: z.array(z.string().regex(AI_PAGE_ID_RE)).min(1).readonly(),
    reason: z.string().min(10),
    decision: z.string().min(5),
    decidedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
  })
  .strict()
  .readonly();

/** US-AIV2-tourguide-0005 (@UC0364) — tên một KẾT QUẢ của trang: snake_case ngắn (`no_open_due`), đọc được trong bảng PO. */
export const AI_PAGE_OUTCOME_RE = /^[a-z][a-z0-9_]*$/;
export const AI_PAGE_OUTCOME_MAX_CHARS = 40;
/** Id thẻ dẫn đường mà `covers` trỏ tới — cùng hình `GUIDE_CARD_ID_RE` (thẻ CÓ THẬT do validator đối với thư viện thẻ). */
const AI_GUIDE_CARD_REF_RE = /^[A-Z0-9-]+$/;

/**
 * 🔴 US-AIV2-tourguide-0005 (@UC0364) — MỘT kết quả: `next` = trang mời đi tiếp (thứ tự = thứ tự nút; rỗng là lời khai hợp lệ — "không mời
 * gì", vd không còn khoản nào phải đóng) · `covers` = thẻ dẫn đường mà câu của kết quả này ĐÃ nói (thay `__guideCovered`).
 */
export const aiPageOutcomeSchema = z
  .object({
    next: z.array(z.string().regex(AI_PAGE_ID_RE)).readonly(),
    covers: z.array(z.string().regex(AI_GUIDE_CARD_REF_RE)).min(1).readonly().optional(),
    /**
     * 🔴 US-AIV2-tourguide-0013 (@UC0377 · QĐ PO 06/10) — nhãn NÚT đi tiếp mượn CTA của một THẺ (trang `next` ⇒ id thẻ có `cta.page` = trang đó), thay
     * tên trang (`help.title`) khi tên trang nói sai TÌNH HUỐNG của kết quả (nhóm chưa có tài khoản ⇒ «Cài…», không «Đổi…»). Không chữ tay ở Sổ: nhãn
     * là tập đóng của thư viện thẻ. Không vào prompt / chỉ mục tìm trang. Validator: `JOURNEY_LABEL_FROM_INVALID`.
     */
    buttonLabelFrom: z.record(z.string().regex(AI_PAGE_ID_RE), z.string().regex(AI_GUIDE_CARD_REF_RE)).readonly().optional(),
  })
  .strict();
export type AiPageOutcome = z.infer<typeof aiPageOutcomeSchema>;

export const aiPageSchema = z
  .object({
    id: z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE),
    /** Mã UC end-to-end của trang (`@UC0192`…), để bảng `ai-registry.md` trỏ thẳng về `USECASES.md`. */
    uc: z.string().regex(/^@UC\d{4}$/),
    tier: z.enum(AI_PAGE_TIERS),
    status: z.enum(AI_PAGE_STATUSES),
    roles: z.array(z.enum(AI_PAGE_GROUP_ROLES)).min(1).readonly(),
    flags: z.array(z.string().regex(AI_FLAG_KEY_RE)).readonly(),
    slots: z.array(aiSlotSchema).readonly(),
    llm: z
      .object({
        /** Mô tả cho Người phiên dịch chọn trang — mô tả VIỆC, không hướng dẫn điền số. */
        description: z.string().min(20),
        examples: z.array(z.string().min(3)).readonly(),
        /**
         * 🔴 US-AIV2-0102 (@UC0176 · QĐ PO 23/09) — câu mà HỎI LẠI mới là ĐÚNG.
         *
         * Khác `examples` ở chỗ chúng **không dạy model chọn trang này** — chúng dạy bộ CHẤM rằng ở
         * câu ấy, `kind: "unknown"` (hỏi lại) là đáp án đúng, còn **chọn một trang là SAI kể cả khi
         * chọn đúng trang này**.
         *
         * Ca mở đầu: `"duyệt đi"` — không nói duyệt **cái gì** (khoản chi · người xin vào nhóm · lời
         * báo chuyển khoản), và một trong ba nhánh dẫn tới bút toán tiền. `@UC0171` đã cấm đoán khi
         * *"0 hoặc ≥2 khớp"*; thiếu trường này thì cổng **phạt model vì làm đúng**.
         *
         * ⚠️ Cố ý KHÔNG vào prompt (xem `INTERPRETER_EXAMPLES_PER_PAGE`): đây là khai báo cho phép
         * ĐO, không phải tài liệu cho model đọc. Đưa vào prompt là dạy model trả `unknown` theo mẫu
         * chữ thay vì theo việc nó thật sự không biết.
         */
        clarifyExamples: z.array(z.string().min(3)).readonly().optional(),
        /**
         * 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI trông giống trang GHI này nhưng là việc XEM ⇒ KHÔNG chọn trang này.
         *
         * Đo 28/09: chủ nhóm HỎI *"có ai báo đã chuyển tiền chưa"* ⇒ phiếu `PAY-REPORT` tự báo cho CHÍNH mình, *"vào sổ quỹ
         * ngay"* (3/3). Sổ khi đó có 0/12 trang T2 nói ra câu hỏi nào KHÔNG thuộc nó — model chỉ thấy ví dụ DƯƠNG, mà
         * `"báo đã chuyển tiền"` là ví dụ nguyên văn của PAY-REPORT.
         *
         * Giống `clarifyExamples`: KHÔNG vào prompt — ĐO 28/09 cho thấy in "ví dụ âm" vào prompt KÉO câu hỏi về phía trang ghi
         * (bảng số ở `interpreter/prompt.ts`). Validator đòi mỗi câu là VÍ DỤ của một trang `read` ⇒ bộ eval CÓ một ca cho câu đó,
         * đáp án = trang đọc. ⚠️ Nói đúng cái được đo (review #1018 HIGH-1): bộ chấm KHÔNG đọc trường này — model chọn trang nào
         * khác (kể cả đúng trang ghi đã khai) đều chỉ là `wrongPage` của trang đọc. Trường là giả thuyết có địa chỉ ("câu này hay bị
         * kéo về đây"), không phải một chốt chặn. Chỉ khai câu mà mở trang này là SAI — trang mở ra đúng thứ câu hỏi cần (vd
         * PAY-CONFIRM-DECIDE liệt kê lời báo chờ cho "có ai báo đã chuyển tiền chưa") thì không khai.
         * Chỉ trang GHI (T1/T2) được khai. Invariant: mọi trang T2 khai ≥1 (`questions-not-here.invariants.spec.ts`).
         */
        questionsNotHere: z.array(z.string().min(3)).readonly().optional(),
      })
      .strict(),
    /** `read`/`T1`/`T2`: cửa ghi/đọc — CÙNG hàm với form GUI (`@UC0156`, binding ở 0023). `redirect`: KHÔNG có. */
    executor: z.string().regex(AI_EXECUTOR_RE).optional(),
    /**
     * 🔴 US-AIV2-0034 (review #832 MED-1) — **cửa ghi này CÓ THỂ chạy hợp lệ mà KHÔNG ghi dòng nào.**
     *
     * Đúng một lý do tới giờ: khoá idempotency theo nội dung (`deriveCollectionCreateKey`) gặp lại khoá cũ ⇒ REPLAY, trả nguyên kết quả
     * lần trước. Khai field này ⇒ validator BẮT BUỘC `templates.doneAlready`; thiếu thì Sổ ĐỎ ngay lúc kiểm, chứ không đợi `renderDone`
     * ném lúc chạy — vì `AiDoerService` bắt lỗi render rồi nói *"Đã ghi xong"*, tức lại đúng lời nói dối mà `doneAlready` sinh ra để xoá.
     *
     * Vế còn lại của luật nằm ở `apps/api` (`executor-binding.invariants.spec.ts`, TỰ KHÁM PHÁ): binding nào trả `AI_FACT_NO_WRITE_KEY`
     * thì trang của nó PHẢI khai field này. Hai chiều: Sổ quên cờ ⇒ api đỏ; cờ có mà quên câu ⇒ domain đỏ.
     */
    executorMayNoop: z.literal(true).optional(),
    /** `redirect`: màn hình mở. Tier khác không khai. */
    screen: z.string().regex(AI_SCREEN_ROUTE_RE).optional(),
    /**
     * 🔴 US-AIV2-0116 (@UC0194) — trang GHI mà chat có thể TỪ CHỐI theo ngữ cảnh (vd `ONB-ADD-MEMBERS` ở nhóm THƯỜNG — lõi chỉ tạo
     * suất cho nhóm phụ huynh): MÀN GUI làm cùng việc. Nút "đi tiếp" của câu từ chối (`failure-text` liệt kê CHÍNH trang này ở `next`)
     * mở THẲNG màn đó (`open_screen`, route SERVER dựng từ Sổ — client không tự viết deep-link), thay vì `run_page` mời lại đúng cửa
     * vừa hỏng. Chỉ trang KHÔNG `redirect` khai (redirect dùng `screen`). Không vào prompt/chỉ mục tìm trang ⇒ 0 ca eval đổi.
     * Gate `gen-ai-coverage` kiểm route có thật như `screen`.
     */
    guiScreen: z.string().regex(AI_SCREEN_ROUTE_RE).optional(),
    /**
     * 🔴 US-AIV2-tourguide-0008 (@UC0362 · TG-INV-4 vế "đến đúng nơi") — điều câu/nhãn của trang này KHẲNG ĐỊNH người dùng sẽ thấy ở màn nó mở
     * (`screen`/`guiScreen`), từ vựng ĐÓNG `AI_SCREEN_CONTENTS`. Validator đòi: trang mở màn khai; nằm trong nội dung màn của route
     * (`AI_SCREEN_CONTENT_BY_ROUTE`) — trừ sổ nợ `AI_SCREEN_CLAIM_DEBTS`. Không vào prompt/chỉ mục tìm trang ⇒ 0 ca eval đổi.
     */
    screenShows: z.enum(AI_SCREEN_CONTENTS).optional(),
    /**
     * 🔴 US-AIV2-tourguide-0008 (@UC0362) — nhãn NÚT mở màn của trang `redirect` khi tên việc (`help.title`) hứa điều màn CHƯA làm được (vd «Đổi vai
     * thành viên» mở danh sách quản trị viên CHỈ ĐỌC ⇒ nút «Xem quản trị viên»). Vắng ⇒ `help.title`. KHÔNG vào chỉ mục tìm trang (khác `help.title`)
     * ⇒ 0 ca eval đổi. Chỉ trang `redirect` khai.
     */
    screenLabel: z.string().min(2).max(40).optional(),
    /**
     * 🔴 AI-SO-DRIFT-1012 (@UC0241 @UC0034 · QĐ người dùng 12/10/2026) — LỐI RIÊNG của người KHÔNG đủ vai mở trang này, thay vế «bạn nhắn … nhé» của
     * câu khoá theo vai (`renderLockedPageText`). Có khi người bị khoá làm được một việc GẦN: thành viên không ghi chi quỹ trả (`EXP-F-NOALLOC`)
     * nhưng GỬI YÊU CẦU CHI được ở màn nhóm. Câu phải ĐÚNG với MỌI vai bị khoá khỏi trang (validator: trang mọi vai mở được ⇒ câu không bao giờ hiện
     * ⇒ đỏ). Không vào prompt/chỉ mục tìm trang ⇒ 0 ca eval đổi.
     */
    roleLockedExit: z.string().min(10).max(240).optional(),
    /**
     * `@UC0216` (US-AIV2-0049) — **BẢN ĐỒ ĐẦY ĐỦ**: những cửa `apps/mobile` đang gọi mà TRANG NÀY chịu trách nhiệm.
     *
     * Thay hẳn bảng tay `docs/ai-coverage-map.json` (ba ô `ai-covered`/`task-open`/`gui-only`): chỗ khai "cửa này
     * thuộc về ai" nay là CHÍNH TRANG, nên nó không trôi khỏi mô tả việc được. Luật: mỗi cửa thuộc **đúng một**
     * trang (`ENDPOINT_CLAIMED_TWICE`); gate `assert-ai-coverage.sh` kiểm nửa còn lại — mọi cửa mobile đang gọi
     * đều có chủ, và không trang nào khai một cửa mobile đã thôi gọi.
     *
     * Mảng RỖNG là một lời khai hợp lệ và thường gặp: trang biến thể của cùng một cửa (`COL-DEADLINE` dùng chung
     * cửa với `COL-EQUAL`), hoặc trang AI làm được mà mobile chưa có màn (`ONB-CREATE-FUND`). Field KHÔNG optional
     * — "quên khai" và "khai là không có" phải phân biệt được.
     */
    endpoints: z.array(z.string().regex(AI_ENDPOINT_RE)).readonly(),
    /** Tên các con số/tên mà executor trả về để chèn vào template (`{soDu}`) — phải khai để chỗ trống kiểm được. */
    facts: z.array(z.string().regex(AI_SLOT_ID_RE)).readonly(),
    templates: aiPageTemplatesSchema,
    /**
     * Nhãn NGƯỚI ĐỌC cho từng chỗ trống (`soTien` → "Mỗi người") — dòng của phiếu xác nhận T2 (0023 `renderConfirm`) dùng nhãn này;
     * khoá phải là ô hoặc fact của trang (validator). T2 mà thiếu nhãn cho một chỗ trống của `templates.confirm` ⇒ render ném (P5.3:
     * không camelCase lên UI). Trang `draft` chưa cần; task trang thêm khi nâng `bound`.
     */
    labels: z.record(z.string().regex(AI_SLOT_ID_RE), z.string().min(1)).readonly().optional(),
    /**
     * Người dẫn đường — bước tiếp theo SAU khi việc này chạy.
     *  · `outcomes` (US-AIV2-tourguide-0005 · @UC0364) — trang ĐỌC/GHI: tập KẾT QUẢ ĐÓNG, mỗi kết quả kèm `next` (nút đi tiếp, mỗi nút qua
     *    cổng quyền + `ready()`) và `covers` (thẻ dẫn đường mà câu của kết quả ĐÃ nói ⇒ giữ nút thẻ, bỏ câu thẻ). Binding đọc trả tên kết quả
     *    (`AI_FACT_OUTCOME_KEY`); trang GHI: `done` · `done_already` (⇔ `executorMayNoop`) · `empty` (⇔ ô `emptyByState`) — validator ép đúng tập.
     *  · `after` — CHỈ còn ở trang `redirect` (cấu hình cũ, không chỗ nào đọc; spec 0005 §2 không đụng). Trang đọc/ghi khai `after` ⇒ đỏ.
     */
    journey: z
      .object({
        after: z.array(z.string().regex(AI_PAGE_ID_RE)).readonly().optional(),
        outcomes: z.record(z.string().max(AI_PAGE_OUTCOME_MAX_CHARS).regex(AI_PAGE_OUTCOME_RE), aiPageOutcomeSchema).readonly().optional(),
      })
      .strict(),
    /**
     * Câu "cách dùng" (`@UC0208`): tiêu đề + câu gõ thử "Thử ngay" — chip gửi câu, 0 LLM khi bấm.
     * `roleNote` (0031, QĐ PO 17/09 09:20): một câu cho PO đọc ở dòng "Ai được" của bảng Sổ khi vai SAU việc khác vai TRƯỚC việc
     * (vd tạo nhóm: người tạo thành chủ nhóm mới; vai ở nhóm cũ không đổi). Chỉ là chữ cho người duyệt — engine không đọc.
     */
    help: z.object({ title: z.string().min(1), tryNow: z.string().min(1), roleNote: z.string().min(10).optional() }).strict(),
    /**
     * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4 25/09: bộ đề = câu TỰ VIẾT) — ĐÁP ÁN TIỀN của bộ eval.
     *
     * Ánh xạ **câu ví dụ nguyên văn** (khoá phải là một phần tử của `llm.examples`) ⇒ số tiền người viết câu
     * muốn nói, dạng **chuỗi minor** (VND: đồng). `gen-golden-set.mjs` chép vào ca (`expectAmountMinor`),
     * `run-ai-eval.mjs` chấm số mà đường ống thật đọc ra từ span của model so với nó.
     *
     * Vì sao ở SỔ, không ở bản ghi: đáp án trong file bản ghi là *"bộ đọc trả ra đúng chuỗi mà CHÍNH dòng bản ghi
     * viết"* (review #855 MED-1 — `amount_self_declared`). Đáp án phải đến từ NGƯỜI VIẾT CÂU, độc lập với đầu ra
     * model, và sống ở nguồn duy nhất của câu.
     *
     * ⚠️ **KHÔNG vào prompt, KHÔNG vào túi chữ BM25** (cùng tinh thần `clarifyExamples`): đây là khai báo cho phép
     * ĐO, không phải tài liệu cho model. Nhờ vậy thêm/sửa đáp án **không đổi hash theo ca** ⇒ 0 bản ghi phải thu
     * lại (ca khoá: `eval/eval-amounts.test.ts`). In nó vào prompt là dạy model chép đáp án.
     *
     * Luật liên-field ở `validate.ts` (`checkEvalAmounts`): khoá là câu ví dụ có thật · trang phải có ô `money` ·
     * câu MƠ HỒ BẬC (§4 "2 trăm", "1tr2", "rưỡi") không được mang số · đã khai thì phải khai ĐỦ mọi câu mà bộ đọc
     * tiền đọc ra đúng một số (chống chọn đề để đậu). Vắng ⇒ trang tiền bị gác sẽ đỏ `amount_unmeasured`.
     */
    evalAmounts: z.record(z.string().min(3), z.string().regex(AI_EVAL_AMOUNT_MINOR_RE)).readonly().optional(),
    /**
     * 🔴 US-AIV2-0114 L09 (@UC0176 @UC0320) — ĐÁP ÁN PHỤ THEO TỪNG CA của bộ eval: câu ví dụ nguyên văn ⇒ trang KHÁC cũng là cách
     * xử lý ĐÚNG cho đúng câu đó. `gen-golden-set.mjs` chép vào ca (`alsoAccept`); bộ chấm (`score.ts`) tính intent ĐÚNG khi model
     * chọn một trang trong đó. Khác `AMBIGUOUS_PAIRS` (`ambiguous.ts` — cặp TRANG, mọi câu của hai trang): ở đây nới đúng MỘT câu.
     *
     * Thêm một mục là một QUYẾT ĐỊNH NỘI DUNG (người dùng nói câu này thì trang kia cũng đúng), phải ghi QĐ + ngày + điều kiện
     * ngay cạnh mục ở trang khai — ratchet hai chiều ở `eval/also-accept.test.ts`. Trước khi khai: probe hành vi THẬT của trang
     * phụ (người dùng THẤY dữ liệu trước mọi nút ghi), không dựa vào tên trang.
     *
     * ⚠️ KHÔNG vào prompt, KHÔNG vào túi chữ BM25 (như `evalAmounts`) ⇒ khai/gỡ không đổi hash theo ca, 0 bản ghi phải thu lại.
     * Luật ở `validate.ts`: khoá là câu `llm.examples` (không phải câu hỏi lại) · không trỏ chính trang · trang phụ có thật ·
     * trang phụ không tự khai câu đó là "không thuộc nó" (`questionsNotHere`) · câu ĐỌC không nhận trang GHI trừ khi trang ghi hoãn
     * quyết định tới khi người dùng thấy dữ liệu (`EVAL_ALSO_ACCEPT_WRITE_FOR_READ`). Mỗi mục: `{pages, reason, decision, decidedOn}`.
     */
    evalAlsoAccept: z.record(z.string().min(3), aiEvalAlsoAcceptEntrySchema).readonly().optional(),
    /**
     * 🔴 US-AIV2-0110 S19b (@UC0267) — OPT-IN của LƯỚI ĐỠ Ô TIỀN (`fillSoleMoneySlotFromSentence`): khi model không gạch ô tiền,
     * được đọc số từ CẢ CÂU người dùng. **Trang chưa khai ⇒ lưới KHÔNG BAO GIỜ áp.**
     *
     * `"sole-amount"` là một LỜI KHAI VỀ NGHĨA của ô: ô money DUY NHẤT của trang là SỐ TIỀN DUY NHẤT của giao dịch — không phải
     * "mỗi người" (COL-*), không phải "tổng hay mỗi người" nhị nghĩa (EXP-F-EQUAL), không phải "phần đã trả vs còn thiếu"
     * (RECORD-PAID · PAY-REPORT). Rà đối kháng S19 đo được lưới KHÔNG khai báo điền *"tổng 3 triệu cho 30 bạn"* thành 3.000.000
     * MỖI NGƯỜI — sai 30 lần. Tiêu chí bật: một câu nói ra đúng một số tiền thì số đó CHẮC CHẮN là giá trị của ô, với mọi cách
     * người dùng hay nói. Không chắc ⇒ không bật (thiếu ⇒ hỏi, an toàn). Hôm nay: INC-RECORD · EXP-F-NOALLOC.
     *
     * Validator (`checkMoneyFromSentence`): chỉ hợp lệ trên trang có ĐÚNG MỘT ô money. ⚠️ KHÔNG vào prompt, KHÔNG vào túi chữ
     * BM25 (như `evalAmounts`) ⇒ bật/tắt không đổi hash theo ca, 0 bản ghi eval phải thu lại.
     */
    moneyFromSentence: z.literal("sole-amount").optional(),
    /**
     * 🔴 AI-COL-OPEN-PHRASING (@UC0195 @UC0220) — OPT-IN của LƯỚI MỞ ĐỢT THU (`collectionOpenPageFromSentence` chọn trang ·
     * `fillCollectionSlotsFromSentence` điền ô), theo khuôn ĐÓNG "nghĩa vụ đóng THEO ĐẦU NGƯỜI" (`parsePerHeadCollection`, interpreter/collection-open.ts):
     * *"mỗi nhà đóng thêm 300k"* · *"thu 50k mỗi người"* · *"tháng nào cũng thu 50k mỗi người"*. **Trang chưa khai ⇒ hai lưới KHÔNG BAO GIỜ áp.**
     *
     * Lời khai về NGHĨA: `cadence` — trang mở đợt thu kiểu nào (`once` một lần không hạn · `recurring` lịch lặp · `deadline` có hạn chót; MỖI kiểu
     * đúng một trang, validator liên-trang gác) · `amount` — ô money nhận SỐ TIỀN MỖI NGƯỜI (khuôn buộc số vào "mỗi <người>", nên KHÔNG có nhị nghĩa
     * "tổng hay mỗi người" mà S19 phải chặn ở COL-*) · `name` — ô text nhận MỤC ĐÍCH người dùng nói ("tiền quà 8/3") làm tên đợt.
     * · `due` (UX-COLLECTION-PERIOD @UC0196, CHỈ trang `deadline` — validator) — ô `date` nhận HẠN = ngày cuối của THÁNG câu nói ("thu quỹ tháng 11
     * mỗi người 150k" ⇒ 30/11): lưới kỳ đợt thu (`collectionPeriodPageFromSentence` chuyển trang `once` sang đây · `fillCollectionPeriodFromSentence`
     * điền ô — interpreter/collection-period.ts). Vắng ⇒ lưới kỳ không áp.
     * ⚠️ KHÔNG vào prompt, KHÔNG vào túi chữ BM25 ⇒ khai/gỡ không đổi hash theo ca, 0 bản ghi eval phải thu lại.
     */
    collectionFromSentence: z
      .object({
        cadence: z.enum(AI_COLLECTION_CADENCES),
        amount: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE),
        name: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE).optional(),
        due: z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE).optional(),
      })
      .strict()
      .readonly()
      .optional(),
    /**
     * 🔴🔴 US-AIV2-0113 ADVANCE-SHORTFALL (@UC0358 · QĐ điều phối 01/10) — trang CHỈ MỞ BẰNG NÚT của một phiếu khác (tập ĐÓNG, hôm nay đúng
     * một giá trị). Đo n=5 (lượt 3): trang ứng bù vào danh mục của Người phiên dịch thì HÚT câu chi thường — "ghi khoan chi 2tr thue xe quy tra
     * het" develop 5/5 · nhánh 0/5 (⇒ ứng bù), "An ứng 1tr5 …, chia đều" 5/5 · 1/5. Vá từng câu là đập chuột ⇒ trang KHÔNG vào tập ứng viên:
     *  - KHÔNG vào chỉ mục/tìm trang (`buildPageIndex` · `scorePages` · `retrievePages` lọc `isAiChatEntryPage`) ⇒ không vào prompt; prompt +
     *    vân tay eval của mọi ca TRÙNG Sổ không có trang này (0 bản ghi phải thu lại);
     *  - KHÔNG có câu ví dụ (validator `CHIP_ONLY_*`: `llm.examples` rỗng, không `clarifyExamples`/`evalAmounts`) ⇒ 0 ca trong bộ câu chuẩn;
     *  - KHÔNG vào menu / trợ giúp / câu khoá (`isAiChatRunnablePage` loại; Người làm dựng tập "cửa" từ `AiPageLookup.entryPages`);
     *  - `startPage` từ chối mở trang này trừ đúng cửa nút đã khai (Người làm: `ADVANCE_NEXT`) — Phiếu hay nút giả chọn nó ⇒ không mở.
     * Status: luật `live` đòi eval xanh (`@UC0159`) KHÔNG nới — trang không có ca nào thì không `live` được.
     */
    entry: z.literal("chip-only").optional(),
    /**
     * 🔴 UX-OPEN-TASK-EDIT (U-2 · review #1435 HIGH-1 · @UC0165) — trang GHI mà một LỜI SỬA gắn cho nó được phép áp vào phiếu ĐANG MỞ của trang
     * này ("đổi tên thành Quỹ Tết" lúc phiếu thu có hạn chờ ⇒ LLM chọn thu đều). Khai TƯỜNG MINH, không suy từ executor: cùng service ghi
     * không có nghĩa cùng HÀNH ĐỘNG (`reportPayment.reportPayment` «Báo đã chuyển» ↔ `reportPayment.cancelReport` «Rút lại lời báo» — NGƯỢC
     * nghĩa; suy theo service thì "rút lại lời báo" sửa thành một lời báo MỚI). Lưới `pages.invariants.spec.ts` (tự khám phá): đối xứng · trang
     * GHI · cùng service VÀ cùng động từ của executor. Vắng ⇒ lời sửa chỉ áp khi cùng trang. ⚠️ KHÔNG vào prompt/BM25 ⇒ 0 ca eval đổi hash.
     */
    editPeers: z.array(z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE)).min(1).readonly().optional(),
  })
  .strict();

export type AiCapabilityPage = z.infer<typeof aiPageSchema>;

/**
 * 🔴 US-AIV2-0113 ADVANCE-SHORTFALL (@UC0358) — trang VÀO ĐƯỢC bằng câu chữ / menu (không phải `entry: "chip-only"`). MỘT vị-từ cho chỉ mục tìm
 * trang, bộ câu chuẩn, menu và tập "cửa" của Người làm. Đặt ở `schema.ts` (thuần, không import trang) để tầng tìm trang dùng không vòng import.
 */
export function isAiChatEntryPage(page: Pick<AiCapabilityPage, "entry">): boolean {
  return page.entry !== "chip-only";
}
